import { isHexId } from '../kql';
import { cellText, isBlankCell } from './columns';
import { cellValue, DATE_TYPES, readDateOrder, splitValues } from './cellValues';

/**
 * From a sheet and a mapping to the rows ingest takes (KPR-1899).
 *
 * `mapping` is { [columnIndex]: { element, suffix?, order? } }. A cell is left
 * out when it is blank, never sent as '' (a default then applies, as on a
 * form). A row with nothing in the mapped columns is no row at all, and is
 * counted rather than written as an empty item.
 */

export const isNameType = type => type === 'card-lookup' || type === 'user';

/** { columnIndex -> element } for the mapped columns, in the file's order. */
export function mappedColumns(columns, mapping, elements) {
	const byName = new Map(elements.map(el => [el.name, el]));
	return columns
		.map(column => ({ column, entry: mapping[column.index], element: byName.get(mapping[column.index]?.element) }))
		.filter(entry => entry.element);
}

/** What a mapped column needs asking about its dates: null, or how it reads. */
export function dateOrderOf(sheet, column) {
	return readDateOrder(sheet.rows.map(row => row.cells[column.index]));
}

/** Does this mapped date column need the person to say which way round it is? */
export function asksDateOrder(sheet, column, element) {
	if (!element || !DATE_TYPES.has(element.dataType)) return false;
	return !dateOrderOf(sheet, column).settled;
}

const hit = (answers, name) => answers?.[String(name).trim().toLowerCase()] || null;

/**
 * One name cell against what nameLookups answered.
 * -> { value } | { problem: { code, value } }
 *
 * For an element that takes several, the whole cell first ("Smith, John" is
 * one person), then its pieces.
 */
export function nameValue(element, cell, answers) {
	const text = cellText(cell).trim();
	const one = name => {
		if (isHexId(name)) return { id: name };
		const answer = hit(answers, name);
		if (answer?.id) return { id: answer.id };
		if (answer?.unchecked) return { problem: { code: 'name_unchecked', value: name } };
		return { problem: { code: answer?.ambiguous ? 'name_ambiguous' : 'name_not_found', value: name, count: answer?.ambiguous } };
	};
	const whole = one(text);
	if (whole.id) return { value: element.allowMultiple ? [whole.id] : whole.id };
	if (!element.allowMultiple) return { problem: whole.problem };
	const results = splitValues(text).map(one);
	const problem = results.find(result => result.problem);
	return problem ? { problem: problem.problem } : { value: results.map(result => result.id) };
}

/**
 * -> { rows: [{ number, elements, problems: [{ columnIndex, column, element,
 * code, value, count? }] }], empty }
 *
 * A name that did not settle is a `problem`, and the row still goes to the dry
 * run without it: the person hears everything wrong with the row at once.
 */
export function buildRows({ sheet, mapping, elements, names = {}, zone = null, dateDefault = 'mdy' }) {
	const mapped = mappedColumns(sheet.columns, mapping, elements);
	const orders = new Map(mapped
		.filter(({ element }) => DATE_TYPES.has(element.dataType))
		.map(({ column, entry }) => [column.index, dateOrderOf(sheet, column).order || entry.order || dateDefault]));
	const rows = [];
	let empty = 0;
	sheet.rows.forEach(row => {
		const values = {};
		const problems = [];
		let filled = false;
		mapped.forEach(({ column, entry, element }) => {
			const cell = row.cells[column.index];
			if (isBlankCell(cell)) return;
			filled = true;
			if (isNameType(element.dataType)) {
				const answer = nameValue(element, cell, names[element.name]);
				if (answer.problem) problems.push({ ...answer.problem, columnIndex: column.index, column: column.name, element: element.name });
				else values[element.name] = answer.value;
				return;
			}
			const value = cellValue(element, cell, { order: orders.get(column.index), zone, suffix: entry.suffix, decimalComma: sheet.decimalComma });
			if (value !== undefined) values[element.name] = value;
		});
		if (filled) rows.push({ number: row.number, elements: values, problems });
		else empty += 1;
	});
	return { rows, empty };
}

/** A safe, readable slug of a file name: the importId people see on Settings → Imports. */
export function fileSlug(fileName) {
	const slug = String(fileName || 'spreadsheet')
		.normalize('NFKD')
		.replace(/[^A-Za-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.toLowerCase()
		.slice(0, 44);
	return slug || 'spreadsheet';
}

/** A short random key: what makes one run's ids its own. */
export function runKey() {
	const bytes = new Uint8Array(4);
	if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
	else bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
	return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Every id one import needs, minted once per check. */
export function mintRun(fileName) {
	const key = runKey();
	return { key, importId: `${fileSlug(fileName)}-${key}`.slice(0, 64), system: 'spreadsheet', ref: String(fileName || '').slice(0, 500) };
}
